import InfoCircleIcon from '@/components/icons/InfoCircleIcon'
import React, { useState, useRef, useEffect } from 'react'
import ReactDOM from 'react-dom'

interface TooltipProps {
  text?: string
  content?: React.ReactNode
  position?: 'top' | 'bottom' | 'left' | 'right'
  additionalClasses?: string
  width?: string
  align?: 'left' | 'center'
  children?: React.ReactNode
  wrapperClassName?: string
}

const Tooltip: React.FC<TooltipProps> = ({
  text,
  content,
  position = 'top',
  additionalClasses,
  width = '213px',
  align = 'center',
  children,
  wrapperClassName = 'relative inline-block',
}) => {
  const [show, setShow] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 })
  const triggerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (show && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      const scrollTop = window.scrollY || document.documentElement.scrollTop
      const scrollLeft = window.scrollX || document.documentElement.scrollLeft

      let top = 0
      let left = 0

      switch (position) {
        case 'top':
          top = rect.top + scrollTop
          left = rect.left + scrollLeft + rect.width / 2
          break
        case 'bottom':
          top = rect.bottom + scrollTop
          left = rect.left + scrollLeft + rect.width / 2
          break
        case 'left':
          top = rect.top + scrollTop + rect.height / 2
          left = rect.left + scrollLeft - 10
          break
        case 'right':
          top = rect.top + scrollTop + rect.height / 2
          left = rect.right + scrollLeft + 10
          break
      }

      setTooltipPosition({ top, left })
    }
  }, [show, position])

  const getTooltipStyles = () => {
    const styles: React.CSSProperties = {
      position: 'absolute',
      zIndex: 9999,
      backgroundColor: 'white',
      color: '#4F4F4F',
      padding: '16px',
      borderRadius: '0.5rem',
      fontSize: '14px',
      boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
      transform: 'translate(-50%, -50%)',
      width,
      border: '0.5px solid #4F4F4F',
      textAlign: align,
    }

    switch (position) {
      case 'top':
        styles.transform = 'translate(-50%, -100%)'
        styles.marginTop = '-5px'
        break
      case 'bottom':
        styles.transform = 'translate(-50%, 0)'
        styles.marginTop = '5px'
        break
      case 'left':
        styles.transform = 'translate(-100%, -50%)'
        styles.marginLeft = '-5px'
        break
      case 'right':
        styles.transform = 'translate(0, -50%)'
        styles.marginLeft = '5px'
        break
    }

    return {
      ...styles,
      top: `${tooltipPosition.top}px`,
      left: `${tooltipPosition.left}px`,
    }
  }

  return (
    <span
      ref={triggerRef}
      className={wrapperClassName}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children ?? <InfoCircleIcon additionalClasses={additionalClasses} />}

      {show &&
        ReactDOM.createPortal(
          <div style={getTooltipStyles()}>
            <div
              style={{
                width: '100%',
                height: '100%',
                overflowWrap: 'break-word',
              }}
            >
              {content ?? text}
            </div>
          </div>,
          document.body
        )}
    </span>
  )
}

export default Tooltip
